import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  Search,
  Car,
  Star,
  ArrowRight
} from 'lucide-react'

// No hardcoded fake data — all content comes from the database

export default function Discover() {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('vehicles')
  const [recentVehicles, setRecentVehicles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadRecentVehicles()
  }, [])

  const loadRecentVehicles = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('id, year, make, model, color, primary_image_url, created_at, owner:profiles!owner_id(full_name)')
        .eq('is_public', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(12)

      if (!error && data) {
        setRecentVehicles(data)
      }
    } catch (err) {
      console.error('Failed to load vehicles:', err)
    }
    setLoading(false)
  }

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr)
    const seconds = Math.floor((new Date() - date) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const getDisplayName = (fullName) => {
    if (!fullName) return 'A collector'
    const parts = fullName.split(' ')
    if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}.`
    return parts[0]
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="text-label mb-1">Explore</p>
        <h1 className="page-title">Discover</h1>
        <p className="text-body mt-2">Connect with collectors and explore remarkable garages</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search collectors, vehicles, or makes..."
          className="input-field pl-11"
        />
      </div>

      {/* Recently Added Vehicles from Database */}
      <div className="space-y-6">
        <h2 className="text-label">Recently Added Vehicles</h2>

        {loading ? (
          <div className="text-center py-12 card">
            <Car size={32} className="mx-auto mb-3 text-rdc-stone animate-pulse" />
            <p className="text-rdc-taupe">Loading vehicles...</p>
          </div>
        ) : recentVehicles.length === 0 ? (
          <div className="text-center py-12 card">
            <Car size={48} className="mx-auto mb-4 text-rdc-stone" />
            <h3 className="font-display text-lg font-semibold text-black mb-2">No public vehicles yet</h3>
            <p className="text-rdc-taupe mb-6">Be the first to share your collection with the community.</p>
            <Link to="/garage" className="btn-primary inline-flex items-center gap-2">
              <Car size={18} />
              Go to My Garage
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentVehicles.map(vehicle => (
              <div key={vehicle.id} className="card overflow-hidden group">
                {vehicle.primary_image_url ? (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={vehicle.primary_image_url}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-rdc-cream flex items-center justify-center">
                    <Car size={40} className="text-rdc-stone" />
                  </div>
                )}
                <div className="p-4">
                  <div className="text-xs font-semibold text-rdc-taupe tracking-wide mb-0.5">
                    {vehicle.year}
                  </div>
                  <div className="font-medium text-black">
                    {vehicle.make} {vehicle.model}
                  </div>
                  <div className="mt-3 flex items-center justify-between pt-3 border-t border-rdc-stone/20">
                    <span className="text-sm text-rdc-taupe">
                      {getDisplayName(vehicle.owner?.full_name)}
                    </span>
                    <span className="text-xs text-rdc-taupe">
                      {formatTimeAgo(vehicle.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Find Specialists CTA */}
        <Link to="/specialists" className="card p-5 bg-gradient-to-br from-rdc-orange/5 via-white to-rdc-cream border-rdc-orange/20 block hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-2">
            <Star size={18} className="text-rdc-orange" />
            <h3 className="font-medium text-black">Find Specialists</h3>
          </div>
          <p className="text-sm text-rdc-charcoal">
            Connect with vetted transport, PPF, restoration, and service experts in the Northeast.
          </p>
          <div className="mt-3 text-sm text-rdc-orange font-medium flex items-center gap-1">
            Browse Directory
            <ArrowRight size={14} />
          </div>
        </Link>
      </div>
    </div>
  )
}
